"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";

const socialLinks = {
  linkedin: "https://www.linkedin.com/company/mileseducation/",
  youtube: "https://www.youtube.com/@MilesEducation",
  instagram: "https://www.instagram.com/miles.accounting/",
  twitter: "https://x.com/mileseducation",
};

const scrollTop = () =>
  typeof window !== "undefined" &&
  window.scrollTo({ top: 0, behavior: "smooth" });

const programs = [
  { name: "CPA", path: "/cpa" },
  { name: "CMA", path: "/cma" },
  { name: "EA (Enrolled Agent)", path: "/ea" },
  { name: "CAIRA", path: "/caira" },
  { name: "US Pathway", path: "/us-jobs-for-accountants" },
];

const legalLinks = [
  { name: "Contact Us", path: "/contact-us" },
  {
    name: "Careers",
    path: "https://mileseducation.zohorecruit.com/jobs/Careers",
  },
  { name: "Privacy Policy", path: "/privacy" },
  { name: "Terms of Service", path: "/terms-and-conditions" },
  { name: "About Us", path: "/about-us" },
  { name: "Blog", path: "/blog" },
  // { name: "Cookie Policy", path: "/cookie-policy" },
];

const CPACityURLs = [
  { name: "Bangalore", path: "/cpa/best-cpa-course-in-bangalore" },
  { name: "Chennai", path: "/cpa/best-cpa-course-in-chennai" },
  { name: "Delhi", path: "/cpa/cpa-coaching-in-delhi" },
  { name: "Ahmedabad", path: "/cpa/best-cpa-course-in-ahmedabad" },
  {
    name: "Hyderabad",
    path: "/cpa/certified-public-accountant-course-in-hyderabad",
  },
  { name: "Mumbai", path: "/cpa/cpa-course-fees-in-mumbai" },
  { name: "Pune", path: "/cpa/cpa-course-fees-in-pune" },
  { name: "Kochi", path: "/cpa/cpa-course-in-kerala" },
  { name: "Kolkata", path: "/cpa/cpa-coaching-in-kolkata" },
];

const CMACityURLs = [
  { name: "Bangalore", path: "/cma/best-cma-course-in-bangalore" },
  { name: "Chennai", path: "/cma/best-cma-institute-in-chennai" },
  { name: "Delhi", path: "/cma/best-cma-coaching-in-delhi" },
  { name: "Ahmedabad", path: "/cma/best-cma-course-in-ahmedabad" },
  { name: "Hyderabad", path: "/cma/best-cma-coaching-in-hyderabad" },
  { name: "Mumbai", path: "/cma/best-cma-classes-in-mumbai" },
  { name: "Pune", path: "/cma/best-cma-classes-in-pune" },
  { name: "Kochi", path: "/cma/best-cma-usa-institute-in-kerala" },
  { name: "Kolkata", path: "/cma/cma-kolkata" },
];

const LocationLink = ({ loc }: { loc: { name: string; path: string } }) => (
  <Link href={loc.path} className="text-white text-xs md:text-sm">
    {loc.name}
  </Link>
);

const Separator = ({ className = "" }: { className?: string }) => (
  <span className={`text-[#666] ${className}`}>|</span>
);

const ColumnHeading = ({ children }: { children: React.ReactNode }) => (
  <h3 className="text-white text-sm md:text-base font-bold">{children}</h3>
);

const ScrollTopButton = () => (
  <button
    type="button"
    aria-label="Scroll to top"
    onClick={scrollTop}
    className="mx-auto flex items-center gap-2 bg-[#2A85FF] text-white font-bold cursor-pointer px-6 py-[7px] md:py-2 text-[15px] rounded-[20px] rounded-bl-none"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M19.5 3H4.5C4.10218 3 3.72064 3.15804 3.43934 3.43934C3.15804 3.72064 3 4.10218 3 4.5V19.5C3 19.8978 3.15804 20.2794 3.43934 20.5607C3.72064 20.842 4.10218 21 4.5 21H19.5C19.8978 21 20.2794 20.842 20.5607 20.5607C20.842 20.2794 21 19.8978 21 19.5V4.5C21 4.10218 20.842 3.72064 20.5607 3.43934C20.2794 3.15804 19.8978 3 19.5 3ZM19.5 19.5H4.5V4.5H19.5V19.5ZM8.46937 11.7806C8.39964 11.711 8.34432 11.6283 8.30658 11.5372C8.26884 11.4462 8.24941 11.3486 8.24941 11.25C8.24941 11.1514 8.26884 11.0538 8.30658 10.9628C8.34432 10.8717 8.39964 10.789 8.46937 10.7194L11.4694 7.71937C11.539 7.64964 11.6217 7.59432 11.7128 7.55658C11.8038 7.51884 11.9014 7.49941 12 7.49941C12.0986 7.49941 12.1962 7.51884 12.2872 7.55658C12.3783 7.59432 12.461 7.64964 12.5306 7.71937L15.5306 10.7194C15.6003 10.7891 15.6556 10.8718 15.6933 10.9628C15.731 11.0539 15.7504 11.1515 15.7504 11.25C15.7504 11.3485 15.731 11.4461 15.6933 11.5372C15.6556 11.6282 15.6003 11.7109 15.5306 11.7806C15.4609 11.8503 15.3782 11.9056 15.2872 11.9433C15.1961 11.981 15.0985 12.0004 15 12.0004C14.9015 12.0004 14.8039 11.981 14.7128 11.9433C14.6218 11.9056 14.5391 11.8503 14.4694 11.7806L12.75 10.0603V15.75C12.75 15.9489 12.671 16.1397 12.5303 16.2803C12.3897 16.421 12.1989 16.5 12 16.5C11.8011 16.5 11.6103 16.421 11.4697 16.2803C11.329 16.1397 11.25 15.9489 11.25 15.75V10.0603L9.53063 11.7806C9.46097 11.8504 9.37825 11.9057 9.28721 11.9434C9.19616 11.9812 9.09856 12.0006 9 12.0006C8.90144 12.0006 8.80384 11.9812 8.71279 11.9434C8.62175 11.9057 8.53903 11.8504 8.46937 11.7806Z"
        fill="#EEEEEE"
      />
    </svg>
    Scroll to Top
  </button>
);

const SocialIcon = ({
  href,
  alt,
  src,
}: {
  href: string;
  alt: string;
  src: string;
}) => (
  <Link
    href={href}
    aria-label={alt}
    target="_blank"
    className="bg-[#0B2139] rounded-full p-3 inline-flex"
  >
    <Image src={src} alt={alt} width={20} height={20} loading="lazy" />
  </Link>
);

const SocialRow = ({ className = "" }: { className?: string }) => (
  <div className={className}>
    <SocialIcon
      href={socialLinks.linkedin}
      alt="LinkedIn"
      src="https://ik.imagekit.io/mileseducation/miles_website/home/social_icons/linkedin.webp"
    />
    <SocialIcon
      href={socialLinks.youtube}
      alt="YouTube"
      src="https://ik.imagekit.io/mileseducation/miles_website/home/social_icons/youtube.webp"
    />
    <SocialIcon
      href={socialLinks.instagram}
      alt="Instagram"
      src="https://ik.imagekit.io/mileseducation/miles_website/home/social_icons/Instagram.webp"
    />
    <SocialIcon
      href={socialLinks.twitter}
      alt="Twitter"
      src="https://ik.imagekit.io/mileseducation/miles_website/home/social_icons/twitter.webp"
    />
  </div>
);

const StoreButton = ({
  href,
  src,
  alt,
  label,
}: {
  href: string;
  src: string;
  alt: string;
  label: string;
}) => (
  <Link
    href={href}
    target="_blank"
    className="bg-[#2A2A2A] border border-[#4D4D4D] rounded-lg flex items-center gap-2 px-3 py-2 w-fit"
  >
    <Image
      src={src}
      alt={alt}
      width={30}
      height={30}
      loading="lazy"
      className="shrink-0"
    />
    <div>
      <p className="text-[10px] leading-3 text-[#CACACA] font-medium">
        Download on
      </p>
      <p className="text-base leading-5 text-white font-medium">{label}</p>
    </div>
  </Link>
);

const DownloadApp = ({ showQR = false }: { showQR?: boolean }) => (
  <div className="flex gap-3 justify-center md:justify-start items-start">
    <div className="flex flex-row md:flex-col gap-3">
      <StoreButton
        href="https://apps.apple.com/us/app/miles-us-pathway/id6504799221"
        src="https://ik.imagekit.io/mileseducation/miles_website/home/app-store.webp?tr=w-48,h-49,q-80"
        alt="App Store"
        label="App Store"
      />
      <StoreButton
        href="https://play.google.com/store/apps/details?id=com.miles.one"
        src="https://ik.imagekit.io/mileseducation/miles_website/home/play-store.webp?tr=w-48,h-49,q-80"
        alt="Play Store"
        label="Play Store"
      />
    </div>
    {showQR && (
      <div className="text-center hidden md:block">
        <Image
          src="https://ik.imagekit.io/mileseducation/miles_website/home/milesone_qr_code.webp"
          alt="QR Code"
          width={92}
          height={92}
          loading="lazy"
          className="min-w-[92px] min-h-[92px] inline-block"
        />
        <p className="pt-1 text-[11px] text-[#CACACA]">Scan to download</p>
      </div>
    )}
  </div>
);

const LegalLinks = ({
  withSeparators = false,
}: {
  withSeparators?: boolean;
}) => (
  <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 md:flex-nowrap md:gap-x-6">
    {legalLinks.map((link, index) => (
      <React.Fragment key={link.name}>
        <Link href={link.path} className="text-white text-xs font-semibold">
          {link.name}
        </Link>
        {withSeparators && index < legalLinks.length - 1 && (
          /* The third separator would dangle at the end of the first mobile
             row, so it only appears once the six sit on one line. */
          <Separator className={index === 2 ? "hidden md:inline" : ""} />
        )}
        {/* A full-width flex item forces the wrap, which pins the split at
            3:3 on mobile instead of leaving it to the text widths. */}
        {index === 2 && <span className="w-full md:hidden" aria-hidden />}
      </React.Fragment>
    ))}
  </div>
);

const Copyright = () => (
  <p className="text-[#CACACA] text-xs md:text-sm">
    ©2026 Miles Education Private Limited. All rights reserved.
  </p>
);

export default function GlobalFooterNewUi({
  fluid = false,
}: {
  /** Use the new home page's fluid gutter instead of the capped main_container. */
  fluid?: boolean;
} = {}) {
  const pathname = usePathname();
  const isCMAPage = pathname?.includes("/cma");
  const locations = useMemo(
    () => (isCMAPage ? CMACityURLs : CPACityURLs),
    [isCMAPage],
  );

  return (
    <footer className="bg-black relative overflow-hidden md:pt-[50px] md:border-0 border-t-[1px] border-[#0a66c299]">
      <div
        className={
          fluid
            ? "px-2 md:px-8 xl:px-[120px] min-[1700px]:px-[170px]"
            : "px-2 md:px-8 main_container mx-auto"
        }
      >
        <div className="md:bg-[#141414] rounded-2xl md:rounded-[20px] px-0 py-7 md:px-10 md:py-10 lg:px-[70px] shadow-[0_10px_34px_0_rgba(0,0,0,0.20)]">
          <ScrollTopButton />

          {/* Mobile Layout */}
          <div className="md:hidden">
            {/* Programs */}
            <div className="text-center mt-10">
              <ColumnHeading>Accounting Programs</ColumnHeading>
              <div className="flex justify-center items-center gap-2 flex-wrap mt-4">
                {programs?.map((prog, index) => (
                  <React.Fragment key={prog.name}>
                    <Link
                      href={prog.path}
                      className="text-white text-xs md:text-sm"
                    >
                      {prog.name}
                    </Link>
                    {index < programs.length - 1 && <Separator />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Location */}
            <div className="text-center mt-8">
              <ColumnHeading>Location</ColumnHeading>
              <div className="flex flex-wrap justify-center items-center gap-2 mt-4">
                {locations?.map((loc, index) => (
                  <React.Fragment key={loc.name}>
                    <LocationLink loc={loc} />
                    {index < locations.length - 1 && <Separator />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Connect */}
            <div className="text-center mt-8">
              <ColumnHeading>Connect with us</ColumnHeading>
              <SocialRow className="flex gap-4 justify-center mt-4" />
            </div>

            {/* App Download */}
            <div className="text-center mt-8">
              <ColumnHeading>Get Miles One App</ColumnHeading>
              <p className="text-[#CACACA] text-xs md:text-sm mt-1 mb-4">
                The #1 App for AI-Ready Accountants
              </p>
              <DownloadApp />
            </div>

            <div className="mt-8">
              <LegalLinks withSeparators />
            </div>
            <div className="text-center mt-3.5 md:mt-5">
              <Copyright />
            </div>
          </div>

          {/* Desktop Layout */}
          <div className="hidden md:block">
            <div className="grid grid-cols-4 gap-8 mt-12">
              {/* Programs Column */}
              <div>
                <ColumnHeading>Accounting Programs</ColumnHeading>
                <div className="flex flex-col gap-4 mt-5">
                  {programs?.map((prog) => (
                    <Link
                      key={prog.name}
                      href={prog.path}
                      className="text-white text-sm"
                    >
                      {prog.name}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Location Column */}
              <div>
                <ColumnHeading>Location</ColumnHeading>
                <div className="flex flex-col gap-4 mt-5">
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[0]} />
                    <Separator />
                    <LocationLink loc={locations[1]} />
                  </div>
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[2]} />
                    <Separator />
                    <LocationLink loc={locations[3]} />
                  </div>
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[4]} />
                    <Separator />
                    <LocationLink loc={locations[5]} />
                  </div>
                  <div className="flex items-center gap-2">
                    <LocationLink loc={locations[6]} />
                    <Separator />
                    <LocationLink loc={locations[7]} />
                    <Separator />
                    <LocationLink loc={locations[8]} />
                  </div>
                </div>
              </div>

              {/* Connect Column */}
              <div>
                <ColumnHeading>Connect with us</ColumnHeading>
                <SocialRow className="grid grid-cols-2 gap-4 w-fit mt-5" />
              </div>

              {/* App Column */}
              <div>
                <ColumnHeading>Get Miles One App</ColumnHeading>
                <p className="text-[#CACACA] text-sm mt-1 mb-3">
                  The #1 App for AI-Ready Accountants
                </p>
                <DownloadApp showQR />
              </div>
            </div>

            {/* Bottom row */}
            <div className="border-t border-[#0A66C2] mt-10 pt-5 flex justify-between items-center gap-4">
              <Copyright />
              <LegalLinks />
            </div>
          </div>
        </div>
      </div>

      {/* Miles wordmark */}
      <Image
        src="https://ik.imagekit.io/mileseducation/miles_website/home_2026/desktop_footer.webp"
        alt=""
        aria-hidden="true"
        width={1606}
        height={356}
        loading="lazy"
        className="hidden md:block mx-auto mt-5 h-auto w-[54%] max-w-[820px] select-none pointer-events-none"
      />
      <Image
        src="https://ik.imagekit.io/mileseducation/miles_website/home_2026/mobile_footer.webp"
        alt=""
        aria-hidden="true"
        width={615}
        height={139}
        loading="lazy"
        className="md:hidden mx-auto mt-6 h-auto w-[86%] select-none pointer-events-none"
      />
    </footer>
  );
}
